import { useEffect, useMemo, useState } from 'react';
import { Box, Icon, Link } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { HelpLink, Layout, Page } from '@nimbus-ds/patterns';
import { Prompt } from 'react-router';
import { OrdersAutoCancelPaymentStatusConfiguration } from '@tiendanube/common/src/domains/orders/orders';
import { OrdersAutoCancelPaymentMethod } from '@tiendanube/common/src/enums';
import { OrdersAutoCancelExpirationTime } from '@tiendanube/common/src/types';
import useNavegate from 'App/hooks/useNavegate';
import {
  CancelAndConfirmButtons,
  HeaderTop,
  IonPageStratus,
  useResponsive,
} from 'commons/components';
import { CAN_LINK_ORDERS_AUTO_CANCEL } from 'commons/constants/externalLinks';
import { useTranslationLanguage } from 'commons/hooks';
import { useGetPaymentMethodsSettingsQuery } from 'domains/NuvemPagoCommons/nuvemPagoCommonsApi';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useNuvemPagoIsActive } from 'domains/Payments/PaymentProviders/hooks';
import Options from './components/Options/Options';
import PaymentMethodsConfig from './components/PaymentMethodsConfig/PaymentMethodsConfig';
import useOrdersAutoCancelConfiguration from './hooks/useOrdersAutoCancelConfiguration';
import OrdersAutoCancelPageError from './OrdersAutoCancelPageError';
import OrdersAutoCancelPageHeader from './OrdersAutoCancelPageHeader';
import OrdersAutoCancelPageSkeleton from './OrdersAutoCancelPageSkeleton';
import './OrdersAutoCancelPage.scss';

function OrdersAutoCancelPage(): JSX.Element {
  const { goBack, referrer } = useNavegate();
  const {
    fetchedConfiguration,
    configuration,
    setConfiguration,
    hasConfigChanged,
    isLoading,
    isError,
    isSaving,
    onSubmit,
    getConfiguration,
    validateConfiguration,
    isAnyPaymentMethodActive,
  } = useOrdersAutoCancelConfiguration();
  const t = useTranslationOrders();
  const language = useTranslationLanguage();

  // Check if user is coming from NuvemPago PIX settings page using useNavegate hook
  const isComingFromNuvemPagoPixSettings = useMemo(
    () => /nuvempago/.test(referrer),
    [referrer],
  );

  const { nuvemPagoIsActive, isLoading: isNuvemPagoLoading } =
    useNuvemPagoIsActive();
  const { isMobile } = useResponsive();

  // Get payment method settings for PIX expiration time
  const { data: paymentMethodsSettings } = useGetPaymentMethodsSettingsQuery();

  const VALID_EXPIRATION_TIMES: OrdersAutoCancelExpirationTime[] = useMemo<
    OrdersAutoCancelExpirationTime[]
  >(() => [15, 30, 60, 120, 1440, 2880, 4320, 10080], []);

  // Convert PIX expiration time from seconds to minutes and use for auto-configuration
  const pixExpirationTimeFromSettings =
    useMemo((): OrdersAutoCancelExpirationTime | null => {
      if (!paymentMethodsSettings?.pix?.expirationTime) return null;
      const expirationTimeInMinutes =
        paymentMethodsSettings.pix.expirationTime / 60;

      return VALID_EXPIRATION_TIMES.includes(
        expirationTimeInMinutes as OrdersAutoCancelExpirationTime,
      )
        ? (expirationTimeInMinutes as OrdersAutoCancelExpirationTime)
        : null;
    }, [paymentMethodsSettings, VALID_EXPIRATION_TIMES]);

  const [canSave, setCanSave] = useState<boolean>(
    configuration?.isNew === false && !!nuvemPagoIsActive,
  );

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isAnyPaymentMethodActiveStatus, setIsAnyPaymentMethodActiveStatus] =
    useState<boolean>(isAnyPaymentMethodActive(configuration ?? undefined));

  const [paymentMethodsWithError, setPaymentMethodsWithError] = useState<
    OrdersAutoCancelPaymentMethod[]
  >([]);

  // Track if PIX auto-configuration from URL has been applied
  const [pixAutoConfigApplied, setPixAutoConfigApplied] =
    useState<boolean>(false);

  useEffect(() => {
    setConfiguration(fetchedConfiguration);
    setCanSave(fetchedConfiguration?.isNew === false && !!nuvemPagoIsActive);
  }, [fetchedConfiguration, nuvemPagoIsActive, setConfiguration]);

  const shouldAutoEnablePix = useMemo(
    (): boolean =>
      !!(
        isComingFromNuvemPagoPixSettings &&
        pixExpirationTimeFromSettings &&
        configuration &&
        nuvemPagoIsActive &&
        !configuration.configuration[OrdersAutoCancelPaymentMethod.PIX]
          ?.isActive &&
        !pixAutoConfigApplied
      ),
    [
      isComingFromNuvemPagoPixSettings,
      pixExpirationTimeFromSettings,
      nuvemPagoIsActive,
      configuration,
      pixAutoConfigApplied,
    ],
  );

  // Handle PIX auto-configuration from NuvemPago PIX settings page (only once)
  useEffect(() => {
    if (shouldAutoEnablePix && configuration) {
      // Auto-enable PIX with the expiration time from payment settings
      const newConfig = {
        ...configuration,
        configuration: {
          ...configuration.configuration,
          [OrdersAutoCancelPaymentMethod.PIX]: {
            isActive: true,
            expirationTime: pixExpirationTimeFromSettings,
          },
        },
      };
      setConfiguration(newConfig);

      // Enable save button since we have a valid configuration
      const anyMethodIsActive = isAnyPaymentMethodActive(newConfig);
      if (configuration.isNew) {
        setCanSave(!!anyMethodIsActive);
      }
      setIsAnyPaymentMethodActiveStatus(!!anyMethodIsActive);

      // Mark that auto-configuration has been applied
      setPixAutoConfigApplied(true);
    }
  }, [
    configuration,
    setConfiguration,
    isAnyPaymentMethodActive,
    pixExpirationTimeFromSettings,
    shouldAutoEnablePix,
  ]);

  useEffect(() => {
    setIsAnyPaymentMethodActiveStatus(
      isAnyPaymentMethodActive(configuration ?? undefined),
    );
  }, [configuration, isAnyPaymentMethodActive]);

  useEffect(() => {
    setIsDirty(hasConfigChanged);
  }, [hasConfigChanged]);

  if (isLoading || isNuvemPagoLoading) {
    return <OrdersAutoCancelPageSkeleton />;
  }

  if (isError || !configuration) {
    return <OrdersAutoCancelPageError onRetry={getConfiguration} />;
  }

  const onPaymentMethodsConfigChange = (
    paymentConfig: OrdersAutoCancelPaymentStatusConfiguration,
  ) => {
    const newConfig = {
      ...configuration,
      configuration: {
        ...configuration.configuration,
        ...paymentConfig,
      },
    };
    const anyMethodIsActive = isAnyPaymentMethodActive(newConfig);
    setIsAnyPaymentMethodActiveStatus(!!anyMethodIsActive);
    if (configuration.isNew) {
      setCanSave(!!anyMethodIsActive);
    }
    setPaymentMethodsWithError([]);
    setConfiguration(newConfig);
  };

  const onUpdateStockChange = (value: boolean) => {
    setConfiguration({
      ...configuration,
      updateStock: value,
    });
  };

  const onNotifyClientChange = (value: boolean) => {
    setConfiguration({
      ...configuration,
      notifyClient: value,
    });
  };

  const onConfigurationSave = () => {
    const emptyConfigurations = validateConfiguration(configuration);
    setPaymentMethodsWithError(emptyConfigurations);
    const isValidConfig = emptyConfigurations.length === 0;
    if (isValidConfig) {
      onSubmit(configuration);
    }
  };

  const headerSaveAction = {
    onClick: onConfigurationSave,
    children: t('autoCancel.save'),
    spinner: isSaving,
    disabled: !canSave || !isDirty || isSaving,
  };

  return (
    <div className="stratus--orders-autocancel-page">
      <IonPageStratus
        headerTop={
          <HeaderTop
            navigation={{ onClick: goBack }}
            mainAction={isMobile ? headerSaveAction : undefined}
          />
        }
        headerContent={
          <OrdersAutoCancelPageHeader
            hasPreviousConfig={!configuration.isNew}
            isNuvemPagoActive={!!nuvemPagoIsActive}
          />
        }
        width="small"
      >
        <Prompt when={isDirty} message={t('common:exitEdit.info')} />
        <div
          className={isMobile ? 'stratus--orders-autocancel-page-mobile' : ''}
        >
          <Page.Body>
            <Layout columns="1">
              <Layout.Section>
                <PaymentMethodsConfig
                  configuration={configuration?.configuration}
                  onChange={onPaymentMethodsConfigChange}
                  disabled={!nuvemPagoIsActive}
                  paymentMethodsWithError={paymentMethodsWithError ?? []}
                  pixPreselectedExpirationTime={
                    isComingFromNuvemPagoPixSettings
                      ? pixExpirationTimeFromSettings
                      : null
                  }
                  defaultPixExpirationTime={pixExpirationTimeFromSettings}
                />
                <Options
                  updateStock={configuration.updateStock}
                  notifyClient={configuration.notifyClient}
                  onUpdateStockChange={onUpdateStockChange}
                  onNotifyClientChange={onNotifyClientChange}
                  disabled={!canSave || !isAnyPaymentMethodActiveStatus}
                />
                <Box justifyContent="flex-end">
                  <CancelAndConfirmButtons
                    isConfirmDisabled={!canSave || !isDirty}
                    onCancel={() => goBack()}
                    onConfirm={onConfigurationSave}
                    isLoading={isSaving}
                    confirmText={t('autoCancel.save')}
                  />
                </Box>
                {!!CAN_LINK_ORDERS_AUTO_CANCEL[language] && (
                  <HelpLink>
                    <Link
                      as="a"
                      href={CAN_LINK_ORDERS_AUTO_CANCEL[language]}
                      target="_blank"
                      appearance="primary"
                      textDecoration="none"
                    >
                      {t('autoCancel.helpLink')}
                      <Icon
                        source={<ExternalLinkIcon />}
                        color="currentColor"
                      />
                    </Link>
                  </HelpLink>
                )}
              </Layout.Section>
            </Layout>
          </Page.Body>
        </div>
      </IonPageStratus>
    </div>
  );
}

export default OrdersAutoCancelPage;
